import { useRef, useState, useCallback, type ReactNode } from "react";
import { Tooltip, type SxProps, type Theme } from "@mui/material";
import Box from "@mui/material/Box";

interface OverflowTooltipProps {
  readonly children: ReactNode;
  readonly sx?: SxProps<Theme>;
}

/**
 * Renders children in a single-line box with text-overflow: ellipsis.
 * Shows a MUI Tooltip with the full text only when the content is
 * actually truncated (scrollWidth > clientWidth).
 */
export default function OverflowTooltip({ children, sx }: OverflowTooltipProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [showTooltip, setShowTooltip] = useState(false);

  const handleMouseEnter = useCallback(() => {
    const el = ref.current;
    if (el) setShowTooltip(el.scrollWidth > el.clientWidth);
  }, []);

  return (
    <Tooltip title={showTooltip ? children : ""} disableInteractive>
      <Box
        ref={ref}
        onMouseEnter={handleMouseEnter}
        sx={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          ...sx as object,
        }}
      >
        {children}
      </Box>
    </Tooltip>
  );
}
